import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useRef, useState } from 'react';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { COMPLIANCE_ALERT_LISTS_QUERY_KEY } from '@/hooks/queries/compliance/use-compliance-alerts';
import { getErrorMessage } from '@/lib/api/error';
import {
  type AlertType,
  BULK_DISMISS_MAX_ALERTS,
  type BulkDismissResult,
  bulkDismissAlerts,
  type ComplianceAlert,
  dismissAlert,
  MUTE_DURATIONS,
  MUTE_DURATIONS_MS,
  type MuteDuration,
  parseDismissWarning,
} from '@/services/api/compliance-alerts';
import { chunk, uniqueSorted } from '@/utils/array';

// A dismiss can succeed while its audit-record write fails, and that warning must reach the operator
const surfaceDismissWarnings = (warnings: string[]) => {
  for (const warning of warnings) toast.warning(warning);
};

/** Each call refetches the alert lists, and throws when any alert failed so the confirm dialog stays open with the error */
export const useDismissAlerts = (alertType: AlertType) => {
  const queryClient = useQueryClient();

  // Retrying after a partial failure re-sends the captured list, and the API would re-dismiss and re-audit what already went through
  const dismissedAlertIdsRef = useRef(new Set<string>());

  const invalidateAlertLists = () => queryClient.invalidateQueries({ queryKey: COMPLIANCE_ALERT_LISTS_QUERY_KEY });

  const dismissOne = async (alert: ComplianceAlert, justification: string, muteExpiry?: number) => {
    const body = await dismissAlert(alert.alert_id, alertType, { comment: justification, muteExpiry });
    const warning = parseDismissWarning(body);

    if (warning) surfaceDismissWarnings([warning]);
    await invalidateAlertLists();
  };

  // Dialog select-all can exceed the API cap; batches run in turn so one user's audit writes never interleave
  const dismissInBulk = async (alerts: ComplianceAlert[], justification: string, muteExpiry?: number) => {
    const pendingAlerts = alerts.filter((alert) => !dismissedAlertIdsRef.current.has(alert.alert_id));
    const failures: BulkDismissResult['error'] = [];

    for (const batch of chunk(pendingAlerts, BULK_DISMISS_MAX_ALERTS)) {
      try {
        const result = await bulkDismissAlerts({
          alerts: batch.map((alert) => ({ alertId: alert.alert_id, type: alertType })),
          comment: justification,
          muteExpiry,
        });

        for (const entry of result.success) dismissedAlertIdsRef.current.add(entry.alertId);
        surfaceDismissWarnings(result.success.flatMap((entry) => (entry.warning ? [`${entry.alertId}: ${entry.warning}`] : [])));
        // The bulk endpoint reports partial failure inside an HTTP 200
        failures.push(...result.error);
      } catch (error) {
        failures.push(...batch.map((alert) => ({ alertId: alert.alert_id, error: getErrorMessage(error) })));
      }
    }

    await invalidateAlertLists();

    if (failures.length > 0) {
      throw new Error(`${failures.length} of ${pendingAlerts.length} alerts failed to dismiss: ${uniqueSorted(failures.map((entry) => entry.error)).join(', ')}`);
    }
  };

  return { dismissOne, dismissInBulk };
};

export interface DismissCopy {
  noun: 'Alert' | 'Alerts';
  describe: (muteDuration?: MuteDuration) => string;
  muteTitle: string;
  renderMuteSummary: (muteDuration: MuteDuration) => ReactNode;
}

interface DismissAlertsButtonsProps {
  copy: DismissCopy;
  disabled?: boolean;
  onDismiss: (justification: string, muteExpiry?: number) => Promise<void>;
}

export const DismissAlertsButtons = ({ copy, disabled = false, onDismiss }: DismissAlertsButtonsProps) => {
  const [isMuteDialogOpen, setIsMuteDialogOpen] = useState(false);
  const [muteDuration, setMuteDuration] = useState<MuteDuration>('1 month');

  const handleDismiss = () =>
    confirmAction({
      title: `Dismiss ${copy.noun}`,
      description: copy.describe(),
      actionLabel: `Dismiss ${copy.noun}`,
      destructive: true,
      onConfirm: (justification) => onDismiss(justification),
    });

  const handleDismissAndMute = async () => {
    setIsMuteDialogOpen(false);
    await confirmAction({
      title: `Dismiss and Mute ${copy.noun}`,
      description: copy.describe(muteDuration),
      actionLabel: 'Dismiss and Mute',
      destructive: true,
      onConfirm: (justification) => onDismiss(justification, Date.now() + MUTE_DURATIONS_MS[muteDuration]),
    });
  };

  return (
    <>
      <Button disabled={disabled} onClick={handleDismiss} type="button" variant="primary">
        Dismiss {copy.noun}
      </Button>
      <Button disabled={disabled} onClick={() => setIsMuteDialogOpen(true)} type="button" variant="destructive">
        Dismiss and Mute {copy.noun}
      </Button>

      <Dialog onOpenChange={setIsMuteDialogOpen} open={isMuteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{copy.muteTitle}</DialogTitle>
          </DialogHeader>
          {copy.renderMuteSummary(muteDuration)}
          <div className="flex flex-col gap-2">
            <Label>Dismiss and mute {copy.noun.toLowerCase()} for</Label>
            <Select onValueChange={(value) => setMuteDuration(value as MuteDuration)} value={muteDuration}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MUTE_DURATIONS.map((duration) => (
                  <SelectItem key={duration} value={duration}>
                    {duration}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button onClick={() => setIsMuteDialogOpen(false)} type="button" variant="outline">
              Close
            </Button>
            <Button onClick={handleDismissAndMute} type="button" variant="destructive">
              Dismiss and Mute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
DismissAlertsButtons.displayName = 'DismissAlertsButtons';
